import type { DocPageContent } from '../types';

/**
 * "Site walk & teach mode" — Dashboard section (ADR-0074).
 *
 * The two operator-run discovery sessions. `discovery` (Getting started) is the
 * concept page — what is read and what is not; this is the operating one — how
 * to actually run each session, what each captures, and which to reach for.
 *
 * Reconciled from apps/web `features/site-walk` + `features/teach-mode` (routes
 * `/site-walk`, `/teach-mode`) and the `siteWalk.*` / `teachMode.*` catalogs.
 * Both sit behind their own Labs switch, which the page says rather than
 * leaving a reader hunting for a page that is not in their sidebar.
 */
/**
 * The ordinary embed. There is no site-walk attribute any more (ADR-0596): the
 * gate is the one-time, origin-bound, five-minute link the dashboard mints, so
 * a site cannot be mapped by anyone who merely knows the key — and there is
 * nothing a customer has to add to their HTML, or remember to take out.
 */
const EMBED = `<script
  type="module"
  src="https://cdn.syncanix.com/widget.js"
  data-key="pk_live_..."
></script>`;

export const DASHBOARD_SITE_MAPPING_PAGE: DocPageContent = {
  slug: 'dashboard/site-mapping',
  titleKey: 'docs.dashboard.mapping.title',
  descriptionKey: 'docs.dashboard.mapping.description',
  blocks: [
    { kind: 'paragraph', textKey: 'docs.dashboard.mapping.lead' },

    {
      kind: 'callout',
      tone: 'note',
      titleKey: 'docs.dashboard.mapping.labs.title',
      textKey: 'docs.dashboard.mapping.labs.body',
      links: {
        labs: {
          target: { docSlug: 'dashboard/labs' },
          labelKey: 'docs.dashboard.mapping.labs.link',
        },
      },
    },

    { kind: 'heading', level: 2, id: 'which', textKey: 'docs.dashboard.mapping.which.heading' },
    { kind: 'paragraph', textKey: 'docs.dashboard.mapping.which.body' },
    {
      kind: 'definitions',
      items: [
        {
          termKey: 'docs.dashboard.mapping.which.walk.term',
          descKey: 'docs.dashboard.mapping.which.walk.desc',
        },
        {
          termKey: 'docs.dashboard.mapping.which.teach.term',
          descKey: 'docs.dashboard.mapping.which.teach.desc',
        },
      ],
    },

    // ── The site walk ──────────────────────────────────────────────────
    { kind: 'heading', level: 2, id: 'walk', textKey: 'docs.dashboard.mapping.walk.heading' },
    { kind: 'paragraph', textKey: 'docs.dashboard.mapping.walk.body' },
    {
      kind: 'steps',
      steps: [
        {
          titleKey: 'docs.dashboard.mapping.walk.step.start.title',
          textKey: 'docs.dashboard.mapping.walk.step.start.text',
        },
        {
          titleKey: 'docs.dashboard.mapping.walk.step.run.title',
          textKey: 'docs.dashboard.mapping.walk.step.run.text',
        },
        {
          titleKey: 'docs.dashboard.mapping.walk.step.watch.title',
          textKey: 'docs.dashboard.mapping.walk.step.watch.text',
        },
      ],
    },
    { kind: 'code', language: 'html', code: EMBED },
    { kind: 'paragraph', textKey: 'docs.dashboard.mapping.walk.does.intro' },
    {
      kind: 'list',
      itemKeys: [
        'docs.dashboard.mapping.walk.does.item.reads',
        'docs.dashboard.mapping.walk.does.item.presses',
        'docs.dashboard.mapping.walk.does.item.live',
      ],
    },
    // The three postures, NAMED. Until this landed the page referred to "the
    // Live posture" in `walk.does.item.live` with no antecedent anywhere on the
    // public site — a dangling reference on the one choice that decides whether
    // the map presses a real page. `docs/runbooks/site-walk.md` § Start a map
    // has carried all three since V3; only the customer-facing half was silent.
    { kind: 'paragraph', textKey: 'docs.dashboard.mapping.walk.posture.intro' },
    {
      kind: 'list',
      itemKeys: [
        'docs.dashboard.mapping.walk.posture.readOnly',
        'docs.dashboard.mapping.walk.posture.safe',
        'docs.dashboard.mapping.walk.posture.live',
      ],
    },

    { kind: 'paragraph', textKey: 'docs.dashboard.mapping.walk.never.intro' },
    {
      kind: 'list',
      itemKeys: [
        'docs.dashboard.mapping.walk.never.item.clicks',
        'docs.dashboard.mapping.walk.never.item.origin',
        'docs.dashboard.mapping.walk.never.item.traffic',
        'docs.dashboard.mapping.walk.never.item.risky',
      ],
    },
    { kind: 'paragraph', textKey: 'docs.dashboard.mapping.walk.history' },

    // ── Teach mode ─────────────────────────────────────────────────────
    { kind: 'heading', level: 2, id: 'teach', textKey: 'docs.dashboard.mapping.teach.heading' },
    { kind: 'paragraph', textKey: 'docs.dashboard.mapping.teach.body' },
    {
      kind: 'steps',
      steps: [
        {
          titleKey: 'docs.dashboard.mapping.teach.step.start.title',
          textKey: 'docs.dashboard.mapping.teach.step.start.text',
        },
        {
          titleKey: 'docs.dashboard.mapping.teach.step.use.title',
          textKey: 'docs.dashboard.mapping.teach.step.use.text',
        },
        {
          titleKey: 'docs.dashboard.mapping.teach.step.stop.title',
          textKey: 'docs.dashboard.mapping.teach.step.stop.text',
        },
      ],
    },
    { kind: 'paragraph', textKey: 'docs.dashboard.mapping.teach.never.intro' },
    {
      kind: 'list',
      itemKeys: [
        'docs.dashboard.mapping.teach.never.item.sensitive',
        'docs.dashboard.mapping.teach.never.item.headers',
        'docs.dashboard.mapping.teach.never.item.raw',
        'docs.dashboard.mapping.teach.never.item.blocked',
      ],
    },
    {
      kind: 'callout',
      tone: 'important',
      titleKey: 'docs.dashboard.mapping.teach.note.title',
      textKey: 'docs.dashboard.mapping.teach.note.body',
    },
    // Z-8's exception, stated where the "never captures" list is (B9's request
    // to G). Recipe capture matches a request's value byte-for-byte against
    // what the page already holds so it can record WHERE the value came from —
    // a meta tag, a hidden field, a cookie, a stored item. That is a read of
    // browser storage, and a "never" list that did not say so would be a
    // promise the code does not keep.
    { kind: 'paragraph', textKey: 'docs.dashboard.mapping.teach.note.storage' },

    { kind: 'heading', level: 2, id: 'next', textKey: 'docs.common.nextSteps' },
    {
      kind: 'cardGrid',
      cards: [
        {
          titleKey: 'docs.dashboard.mapping.next.discovery.title',
          textKey: 'docs.dashboard.mapping.next.discovery.text',
          to: { docSlug: 'discovery' },
        },
        {
          titleKey: 'docs.dashboard.mapping.next.privacy.title',
          textKey: 'docs.dashboard.mapping.next.privacy.text',
          to: { docSlug: 'trust/privacy' },
        },
      ],
    },
  ],
};
